import React, { useContext, useState, useEffect, useMemo } from "react";
import cx from "classnames";
import dayjs from "dayjs";
import { DatePicker, RoundButton, Select } from "@foris/avocado-suite";
import { Icon } from "@foris/avocado-icons";
import { BookingContext } from "../../context/BookingContext";
import { Types } from "../../context/search.reducer";
import { format } from "../../utils";
import css from "./dayRow.module.scss";
import { useTranslation } from "react-i18next";
import useBookingAccess from "@common/hooks/useBookingAccess";

interface DayRowProps {
  index: number;
  day: "day" | "date";
}

const DayRow: React.FC<DayRowProps> = (props: DayRowProps) => {
  const { index, day } = props;
  const { state, dispatch } = useContext(BookingContext);
  const { t } = useTranslation();
  const { isAdminBooking } = useBookingAccess();

  const dateIndex = useMemo(() => {
    const dates = state?.search?.dateTime || [];
    return dates.findIndex(date => date.index === index);
  }, [index, state?.search?.dateTime]);

  const data = (state?.search?.dateTime || [])?.[dateIndex];

  const { start = [], end = [] } = state?.search?.blockOptions || {};
  const [startValue, setStartValue] = useState(data?.start?.value);
  const [endValue, setEndValue] = useState(data?.end?.value);
  const [calendarValue, setCalendarValue] = useState(data?.day?.value);
  const ERROR_START = "Ingresa una hora de inicio";
  const ERROR_END = "Ingresa una hora de termino";

  const endOptions = useMemo(() => {
    if (!startValue) return [];

    const newEndOptions = [];
    const date = new Date().toDateString();

    end.forEach(option => {
      const selectTime = dayjs(`${date} ${startValue.value}`);
      const optionTime = dayjs(`${date} ${option.value}`);
      if (optionTime.isAfter(selectTime)) {
        newEndOptions.push(option);
      }
    });

    return newEndOptions;
  }, [end, startValue]);

  useEffect(() => {
    const clonedDates = [...state?.search?.dateTime];

    if (calendarValue !== data?.day?.value) {
      clonedDates[dateIndex].day.value = calendarValue;
      clonedDates[dateIndex].day.error = false;
      dispatch({ type: Types.SetDateTime, payload: clonedDates });
    }
    if (startValue !== data?.start?.value) {
      clonedDates[dateIndex].start.value = startValue;
      clonedDates[dateIndex].start.error = false;
      dispatch({ type: Types.SetDateTime, payload: clonedDates });
    }
    if (endValue !== data?.end?.value) {
      clonedDates[dateIndex].end.value = endValue;
      clonedDates[dateIndex].end.error = false;
      dispatch({ type: Types.SetDateTime, payload: clonedDates });
    }
  }, [startValue, endValue, calendarValue]);

  const isHoliday = state?.search?.dateTimeFormatted?.[dateIndex]?.day?.isHoliday;
  const isRecurrentHoliday = !!(
    isHoliday &&
    day === "day" &&
    calendarValue &&
    startValue &&
    endValue
  );
  const isDateHoliday = !!(isHoliday && day === "date" && startValue && endValue);

  return (
    <div className={css.dayRow}>
      <div className={css.dayRow_form}>
        {day === "date" && (
          <DatePicker
            placeholder="dd.mm.yyyy"
            label={t("booking.booking-form.date-selector.day-row.day", {
              day: index,
            })}
            error={data?.day?.error ? "Ingresa una fecha" : null}
            className={css.formDatePicker}
            minDate={new Date()}
            selectedDate={Boolean(data?.day?.value) ? dayjs(data?.day?.value).toDate() : undefined}
            onChange={select => {
              setCalendarValue(dayjs(select).format("YYYY-MM-DD"));
            }}
          />
        )}
        {day === "day" && (
          <Select
            label={t("booking.booking-form.date-selector.day-row.day", {
              day: index,
            })}
            placeholder={t("booking.booking-form.date-selector.day-row.placeholder-day")}
            error={data?.day?.error ? "Ingresa un día" : null}
            className={cx(css.cntForm_selectTime, "col_3", "col_sm_5")}
            isClearable={false}
            options={format.DAYS_OPTIONS()}
            value={format.getSelectableDay(data?.day?.value)}
            onChange={value => {
              setCalendarValue(value?.value);
            }}
          />
        )}
        <section className={css.formTimeWrapper}>
          <Select
            label={t("booking.booking-form.date-selector.day-row.start-time")}
            placeholder="hh:mm"
            error={data?.start?.error ? ERROR_START : null}
            className={css.formTimeWrapper_timeSelector}
            isClearable={false}
            options={start}
            value={data?.start?.value}
            disabled={isRecurrentHoliday || isDateHoliday}
            onChange={value => {
              setStartValue(value);
              setEndValue(null);
            }}
          />
          <span className={css.formTimeWrapper_divider}>-</span>
          <Select
            label={t("booking.booking-form.date-selector.day-row.end-time")}
            placeholder="hh:mm"
            error={data?.end?.error ? ERROR_END : null}
            className={css.formTimeWrapper_timeSelector}
            isClearable={false}
            options={endOptions}
            disabled={!startValue || isRecurrentHoliday || isDateHoliday}
            value={data?.end?.value}
            onChange={value => {
              setEndValue(value);
            }}
          />

          {(isRecurrentHoliday || isDateHoliday) && (
            <div className={css.formTimeWrapper_warning}>
              <Icon
                name={isAdminBooking ? "alert-triangle" : "circle-cross"}
                color={isAdminBooking ? "warning-50" : "error-50"}
                filled
              />
            </div>
          )}
        </section>
      </div>

      {state?.search?.dateTime?.length > 1 && (
        <RoundButton
          className={css.dayRow_removeBtn}
          icon="circle-minus"
          variant="ghost"
          size="sm"
          onClick={() => {
            const newCalendar = [...state?.search?.dateTime];
            const indexFind = newCalendar.findIndex(value => value.index === index);
            if (indexFind >= 0) newCalendar.splice(indexFind, 1);
            newCalendar.forEach((value, i) => {
              value.index = i + 1;
            });
            dispatch({ type: Types.SetDateTime, payload: newCalendar });
          }}
        />
      )}
    </div>
  );
};

export default DayRow;
